"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { 
  Upload, 
  FileSpreadsheet, 
  Eye, 
  Download,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Info,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Edit3,
  Trash2,
  Save,
  X
} from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { useToast } from "../../../components/ui/use-toast"
import { Alert, AlertDescription } from "../../../components/ui/alert"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog"
import BulkUploadModal from "../../../components/BulkUploadModal"
import { 
  getAnnualBudgets,
  createBulkAnnualBudgets,
  updateAnnualBudget,
  deleteAnnualBudget,
  getBudgetSummaryByYear,
  formatBulkBudgetData,
  validateBudgetData
} from "../../../../ApiCalls/mantisFlowsBudgets"

export default function BudgetConfigurationModal({ clientId, onClose, embedded = false }) {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [budgetData, setBudgetData] = useState([])
  const [loading, setLoading] = useState(false)
  const [analysisData, setAnalysisData] = useState(null)
  const [currentYear] = useState(2025) // Cambiado a 2025 para coincidir con los datos
  const [uploadedData, setUploadedData] = useState([])
  const [isLoadingBudgets, setIsLoadingBudgets] = useState(true)
  const [uploadResult, setUploadResult] = useState(null)
  const [editingRow, setEditingRow] = useState(null)
  const [editedData, setEditedData] = useState({})
  const [isDeleting, setIsDeleting] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [budgetToDelete, setBudgetToDelete] = useState(null)

  const { toast } = useToast()

  // Configuración para el BulkUploadModal
  const uploadConfig = {
    requiredFields: ['departamento', 'categoria'],
    fieldMappings: {
      departamento: { label: 'Departamento' },
      categoria: { label: 'Categoría' },
      enero: { label: 'Enero' },
      febrero: { label: 'Febrero' },
      marzo: { label: 'Marzo' },
      abril: { label: 'Abril' },
      mayo: { label: 'Mayo' },
      junio: { label: 'Junio' },
      julio: { label: 'Julio' },
      agosto: { label: 'Agosto' },
      septiembre: { label: 'Septiembre' },
      octubre: { label: 'Octubre' },
      noviembre: { label: 'Noviembre' },
      diciembre: { label: 'Diciembre' }
    },
    validateItem: (item) => {
      const errors = []
      
      if (!item.departamento || item.departamento.trim() === '') {
        errors.push('Departamento es requerido')
      }
      
      if (!item.categoria || item.categoria.trim() === '') {
        errors.push('Categoría es requerida')
      }

      // Validar que al menos tenga un mes con valor numérico válido
      const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                     'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
      
      const hasValidMonth = months.some(month => {
        const value = item[month]
        return value !== null && value !== undefined && value !== '' && !isNaN(Number(value)) && Number(value) >= 0
      })

      if (!hasValidMonth) {
        errors.push('Debe tener al menos un mes con un valor numérico válido')
      }

      return {
        isValid: errors.length === 0,
        errors
      }
    }
  }

  // Cargar datos de presupuesto al montar el componente
  useEffect(() => {
    loadBudgetData()
  }, [clientId, currentYear])

  // Función para cargar datos desde la API
  const loadBudgetData = async () => {
    setIsLoadingBudgets(true)
    try {
      console.log('Loading budget data for clientId:', clientId, 'year:', currentYear); // Debug log
      const response = await getAnnualBudgets(clientId, { year: currentYear })
      
      console.log('API Response:', response); // Debug log
      
      if (response.success) {
        console.log('Response data:', response.data); // Debug log
        console.log('Response data type:', typeof response.data); // Debug log
        console.log('Response data is array:', Array.isArray(response.data)); // Debug log
        
        // Manejar diferentes estructuras de respuesta
        let dataArray = response.data;
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          dataArray = response.data.data;
        } else if (!Array.isArray(response.data)) {
          console.log('Response data is not an array, checking if it has data property');
          dataArray = [];
        }
        
        console.log('Final dataArray:', dataArray); // Debug log
        
        if (dataArray && dataArray.length > 0) {
          // Convertir datos de API al formato esperado por el componente
          const formattedData = dataArray.map(budget => {
            console.log('Processing budget:', budget); // Debug log
            return {
              id: budget.id, // Incluir el ID para edición y eliminación
              departamento: budget.department,
              categoria: budget.category,
              enero: Number(budget.monthly?.january || 0),
              febrero: Number(budget.monthly?.february || 0),
              marzo: Number(budget.monthly?.march || 0),
              abril: Number(budget.monthly?.april || 0),
              mayo: Number(budget.monthly?.may || 0),
              junio: Number(budget.monthly?.june || 0),
              julio: Number(budget.monthly?.july || 0),
              agosto: Number(budget.monthly?.august || 0),
              septiembre: Number(budget.monthly?.september || 0),
              octubre: Number(budget.monthly?.october || 0),
              noviembre: Number(budget.monthly?.november || 0),
              diciembre: Number(budget.monthly?.december || 0)
            };
          })
          
          console.log('Formatted data for component:', formattedData); // Debug log
          setBudgetData(formattedData)
          setAnalysisData(calculateAnalysis(formattedData))
        } else {
          console.log('No data found in response'); // Debug log
          setBudgetData([])
          setAnalysisData(null)
        }
      } else {
        console.log('API response not successful:', response.error); // Debug log
        setBudgetData([])
        setAnalysisData(null)
      }
    } catch (error) {
      console.error('Error loading budget data:', error)
      toast({
        title: "Error al cargar datos",
        description: "No se pudieron cargar los datos de presupuesto.",
        variant: "destructive"
      })
      setBudgetData([])
      setAnalysisData(null)
    } finally {
      setIsLoadingBudgets(false)
    }
  }

  // Datos vacíos por defecto
  const emptyData = []

  const months = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ]

  // Formatear números como moneda colombiana
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Calcular análisis de datos
  const calculateAnalysis = (data) => {
    if (!data || data.length === 0) return null

    console.log('Calculating analysis for data:', data); // Debug log

    const totalByMonth = months.map(month => {
      const total = data.reduce((sum, row) => sum + (row[month] || 0), 0)
      console.log(`Total for ${month}:`, total); // Debug log
      return total;
    })

    const totalAnual = totalByMonth.reduce((sum, month) => sum + month, 0)
    const promedioDepartamento = totalAnual / data.length
    const maxMes = Math.max(...totalByMonth)
    const minMes = Math.min(...totalByMonth)
    const maxMesNombre = months[totalByMonth.indexOf(maxMes)]
    const minMesNombre = months[totalByMonth.indexOf(minMes)]

    const analysis = {
      totalAnual,
      promedioDepartamento,
      maxMes,
      minMes,
      maxMesNombre,
      minMesNombre,
      totalByMonth,
      variacion: minMes > 0 ? ((maxMes - minMes) / minMes * 100).toFixed(1) : '0'
    }

    console.log('Analysis result:', analysis); // Debug log
    return analysis;
  }

  const handleUploadSuccess = async (data, options = {}) => {
    setLoading(true)
    try {
      // Formatear datos del Excel
      const formattedResult = formatBulkBudgetData(data, clientId, currentYear)
      
      if (!formattedResult.success) {
        setUploadResult({
          success: false,
          errors: [formattedResult.error],
          successfulItems: 0,
          failedItems: data.length
        })
        setLoading(false)
        return
      }

      // Enviar datos a la API
      const response = await createBulkAnnualBudgets(clientId, formattedResult.data)
      
      if (response.success) {
        setUploadResult({
          success: true,
          successfulItems: formattedResult.data.length,
          failedItems: 0,
          errors: []
        })
        
        setUploadedData(data)
        
        // Recargar datos desde la API
        await loadBudgetData()
      } else {
        setUploadResult({
          success: false,
          errors: [response.error || "No se pudieron guardar los datos de presupuesto."],
          successfulItems: 0,
          failedItems: formattedResult.data.length
        })
      }
    } catch (error) {
      console.error('Error processing upload:', error)
      setUploadResult({
        success: false,
        errors: ["Hubo un error al procesar el archivo. Verifica el formato."],
        successfulItems: 0,
        failedItems: data.length
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadTemplate = () => {
    // Crear datos de ejemplo para la plantilla
    const templateData = [
      {
        departamento: "Ejemplo Departamento",
        categoria: "Ejemplo Categoría",
        enero: 100000000,
        febrero: 110000000,
        marzo: 120000000,
        abril: 115000000,
        mayo: 125000000,
        junio: 130000000,
        julio: 128000000,
        agosto: 135000000,
        septiembre: 140000000,
        octubre: 145000000,
        noviembre: 150000000,
        diciembre: 155000000
      }
    ]

    // Convertir a CSV
    const headers = ['departamento', 'categoria', ...months]
    const csvContent = [
      headers.join(','),
      ...templateData.map(row => 
        headers.map(header => row[header] || 0).join(',')
      )
    ].join('\n')

    // Descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `plantilla_presupuesto_${currentYear}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast({
      title: "Plantilla descargada",
      description: "La plantilla de presupuesto se ha descargado correctamente.",
      variant: "default"
    })
  }

  // Función para iniciar edición
  const handleStartEdit = (index, row) => {
    setEditingRow(index)
    setEditedData({
      id: row.id, // Necesitamos que la API devuelva el ID
      departamento: row.departamento,
      categoria: row.categoria,
      enero: row.enero,
      febrero: row.febrero,
      marzo: row.marzo,
      abril: row.abril,
      mayo: row.mayo,
      junio: row.junio,
      julio: row.julio,
      agosto: row.agosto,
      septiembre: row.septiembre,
      octubre: row.octubre,
      noviembre: row.noviembre,
      diciembre: row.diciembre
    })
  }

  // Función para cancelar edición
  const handleCancelEdit = () => {
    setEditingRow(null)
    setEditedData({})
  }

  // Función para guardar cambios
  const handleSaveEdit = async () => {
    if (!editedData.id) {
      toast({
        title: "Error",
        description: "No se puede editar este registro. ID no encontrado.",
        variant: "destructive"
      })
      return
    }

    setLoading(true)
    try {
      // Formatear datos para la API
      const updatedBudget = {
        department: editedData.departamento,
        category: editedData.categoria,
        year: currentYear,
        monthly: {
          january: parseFloat(editedData.enero) || 0,
          february: parseFloat(editedData.febrero) || 0,
          march: parseFloat(editedData.marzo) || 0,
          april: parseFloat(editedData.abril) || 0,
          may: parseFloat(editedData.mayo) || 0,
          june: parseFloat(editedData.junio) || 0,
          july: parseFloat(editedData.julio) || 0,
          august: parseFloat(editedData.agosto) || 0,
          september: parseFloat(editedData.septiembre) || 0,
          october: parseFloat(editedData.octubre) || 0,
          november: parseFloat(editedData.noviembre) || 0,
          december: parseFloat(editedData.diciembre) || 0
        }
      }

      // Calcular total
      updatedBudget.total = Object.values(updatedBudget.monthly).reduce((sum, value) => sum + value, 0)

      const response = await updateAnnualBudget(clientId, editedData.id, updatedBudget)
      
      if (response.success) {
        toast({
          title: "Presupuesto actualizado",
          description: "Los cambios se han guardado correctamente.",
          variant: "default"
        })
        
        // Recargar datos
        await loadBudgetData()
        handleCancelEdit()
      } else {
        toast({
          title: "Error al actualizar",
          description: response.error || "No se pudieron guardar los cambios.",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error('Error updating budget:', error)
      toast({
        title: "Error",
        description: "Hubo un error al actualizar el presupuesto.",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  // Función para abrir modal de confirmación de eliminación
  const handleDeleteClick = (budgetId, departamento, categoria, budgetRow) => {
    if (!budgetId) {
      toast({
        title: "Error",
        description: "No se puede eliminar este registro. ID no encontrado.",
        variant: "destructive"
      })
      return
    }

    setBudgetToDelete({
      id: budgetId,
      departamento,
      categoria,
      row: budgetRow
    })
    setDeleteModalOpen(true)
  }

  // Función para confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!budgetToDelete) return

    setIsDeleting(budgetToDelete.id)
    try {
      const response = await deleteAnnualBudget(clientId, budgetToDelete.id)
      
      if (response.success) {
        toast({
          title: "Presupuesto eliminado",
          description: `El presupuesto de ${budgetToDelete.departamento} - ${budgetToDelete.categoria} ha sido eliminado.`,
          variant: "default"
        })
        
        // Recargar datos
        await loadBudgetData()
        setDeleteModalOpen(false)
        setBudgetToDelete(null)
      } else {
        toast({
          title: "Error al eliminar",
          description: response.error || "No se pudo eliminar el presupuesto.",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error('Error deleting budget:', error)
      toast({
        title: "Error",
        description: "Hubo un error al eliminar el presupuesto.",
        variant: "destructive"
      })
    } finally {
      setIsDeleting(null)
    }
  }

  // Función para cancelar eliminación
  const handleCancelDelete = () => {
    setDeleteModalOpen(false)
    setBudgetToDelete(null)
  }

  const displayData = budgetData
  const displayAnalysis = analysisData || {
    totalAnual: 0,
    maxMes: 0,
    minMes: 0,
    maxMesNombre: 'N/A',
    minMesNombre: 'N/A',
    variacion: '0'
  }

  // Debug logs para el estado del componente
  console.log('Component state - budgetData:', budgetData);
  console.log('Component state - isLoadingBudgets:', isLoadingBudgets);
  console.log('Component state - displayData:', displayData);
  console.log('Component state - analysisData:', analysisData);

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="p-8 space-y-8">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <DollarSign className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-800">Configuración de Presupuesto {currentYear}</h1>
                <p className="text-slate-600 text-lg">
                  {isLoadingBudgets ? 
                    'Cargando datos de presupuesto...' : 
                    budgetData.length > 0 ? 
                      `Gestiona los datos de presupuesto de tu organización (${budgetData.length} departamentos cargados)` :
                      'Carga y gestiona los datos de presupuesto de tu organización'
                  }
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={loadBudgetData}
                disabled={isLoadingBudgets}
                className="flex items-center space-x-2"
              >
                {isLoadingBudgets ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span>Actualizar</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Descargar Plantilla</span>
              </Button>
              <Button
                onClick={() => setShowUploadModal(true)}
                disabled={loading}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white flex items-center space-x-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                <span>{loading ? 'Procesando...' : 'Cargar Presupuesto'}</span>
              </Button>
            </div>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded p-6 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Total Anual</p>
                  {isLoadingBudgets ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-700" />
                      <span className="text-blue-700">Cargando...</span>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(displayAnalysis.totalAnual)}
                    </p>
                  )}
                </div>
                <BarChart3 className="h-8 w-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Mes Mayor</p>
                  <p className="text-xl font-bold text-green-900 capitalize">
                    {displayAnalysis.maxMesNombre}
                  </p>
                  <p className="text-sm text-green-700">
                    {formatCurrency(displayAnalysis.maxMes)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded p-6 border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-medium">Mes Menor</p>
                  <p className="text-xl font-bold text-orange-900 capitalize">
                    {displayAnalysis.minMesNombre}
                  </p>
                  <p className="text-sm text-orange-700">
                    {formatCurrency(displayAnalysis.minMes)}
                  </p>
                </div>
                <TrendingDown className="h-8 w-8 text-orange-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded p-6 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-medium">Variación</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {displayAnalysis.variacion}%
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Demo/Data Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Eye className="h-6 w-6 text-slate-600" />
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">
                    {isLoadingBudgets ? 
                      'Cargando Datos de Presupuesto...' :
                      budgetData.length > 0 ? 
                        `Datos de Presupuesto ${currentYear}` : 
                        `Presupuesto ${currentYear} - Sin Datos`
                    }
                  </h2>
                  <p className="text-slate-600">
                    {isLoadingBudgets ?
                      'Obteniendo información desde la base de datos...' :
                      budgetData.length > 0 ? 
                        `${budgetData.length} departamentos cargados para el año ${currentYear}` : 
                        'No hay datos de presupuesto disponibles. Carga un archivo Excel para comenzar.'
                    }
                  </p>
                </div>
              </div>
              
              {isLoadingBudgets ? (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Cargando
                </Badge>
              ) : budgetData.length === 0 ? (
                <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                  Sin Datos
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  {budgetData.length} Departamentos
                </Badge>
              )}
            </div>
          </div>

          {budgetData.length === 0 && !isLoadingBudgets && (
            <div className="px-6 py-4">
              <Alert className="border-amber-200 bg-amber-50">
                <Info className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <div className="space-y-2">
                    <p className="font-medium">No hay datos de presupuesto para el año {currentYear}.</p>
                    <p className="text-sm">Para comenzar, carga un archivo Excel con los datos de presupuesto de tu organización.</p>
                    <div className="text-sm">
                      <strong>Columnas requeridas:</strong> Departamento, Categoría, Enero-Diciembre
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 sticky left-0 bg-slate-50 z-10">
                    Departamento
                  </th>
                  {months.map(month => (
                    <th key={month} className="px-4 py-4 text-center text-sm font-semibold text-slate-900 min-w-[120px] capitalize">
                      {month}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Categoría
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Total Anual
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {displayData.length === 0 && !isLoadingBudgets ? (
                  <tr>
                    <td colSpan={months.length + 4} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center space-y-4">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                          <BarChart3 className="h-8 w-8 text-slate-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-slate-900">No hay datos de presupuesto</h3>
                          <p className="text-slate-500 mt-1">Carga un archivo Excel para ver los datos aquí</p>
                        </div>
                        <Button
                          onClick={() => setShowUploadModal(true)}
                          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Cargar Presupuesto
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : displayData.map((row, index) => {
                  const totalAnual = months.reduce((sum, month) => sum + (row[month] || 0), 0)
                  return (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-slate-900 sticky left-0 bg-white hover:bg-slate-50 z-10">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-emerald-500"></div>
                          {editingRow === index ? (
                            <input
                              type="text"
                              value={editedData.departamento || ''}
                              onChange={(e) => setEditedData({...editedData, departamento: e.target.value})}
                              className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                            />
                          ) : (
                            <span>{row.departamento}</span>
                          )}
                        </div>
                      </td>
                      {months.map(month => (
                        <td key={month} className="px-4 py-4 text-sm text-slate-700 text-center">
                          {editingRow === index ? (
                            <input
                              type="number"
                              value={editedData[month] || 0}
                              onChange={(e) => setEditedData({...editedData, [month]: parseFloat(e.target.value) || 0})}
                              className="border border-gray-300 rounded px-2 py-1 text-sm w-full text-center font-mono"
                              min="0"
                              step="1000"
                            />
                          ) : (
                            <span className="font-mono">
                              {formatCurrency(row[month] || 0)}
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="px-6 py-4 text-center">
                        {editingRow === index ? (
                          <input
                            type="text"
                            value={editedData.categoria || ''}
                            onChange={(e) => setEditedData({...editedData, categoria: e.target.value})}
                            className="border border-gray-300 rounded px-2 py-1 text-sm w-full text-center"
                          />
                        ) : (
                          <Badge 
                            variant="outline" 
                            className={`
                              ${row.categoria === 'Operacional' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''}
                              ${row.categoria === 'Desarrollo' ? 'bg-purple-50 text-purple-700 border-purple-200' : ''}
                              ${row.categoria === 'Administrativo' ? 'bg-gray-50 text-gray-700 border-gray-200' : ''}
                              ${row.categoria === 'Comercial' ? 'bg-green-50 text-green-700 border-green-200' : ''}
                              ${row.categoria === 'Tecnológico' ? 'bg-orange-50 text-orange-700 border-orange-200' : ''}
                            `}
                          >
                            {row.categoria}
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900 text-center">
                        <span className="font-mono">
                          {editingRow === index ? 
                            formatCurrency(Object.values(editedData).slice(3).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)) :
                            formatCurrency(totalAnual)
                          }
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {editingRow === index ? (
                            <>
                              <Button
                                size="sm"
                                onClick={handleSaveEdit}
                                disabled={loading}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                {loading ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Save className="h-3 w-3" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                                disabled={loading}
                                className="border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStartEdit(index, row)}
                                disabled={editingRow !== null || loading}
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                <Edit3 className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteClick(row.id, row.departamento, row.categoria, row)}
                                disabled={editingRow !== null || isDeleting === row.id || loading}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                {isDeleting === row.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
              
              {/* Total Row - Solo mostrar si hay datos */}
              {displayData.length > 0 && (
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr>
                    <td className="px-6 py-4 text-sm font-bold text-slate-900 sticky left-0 bg-slate-100 z-10">
                      TOTAL GENERAL
                    </td>
                    {months.map(month => {
                      const total = displayData.reduce((sum, row) => sum + (row[month] || 0), 0)
                      return (
                        <td key={month} className="px-4 py-4 text-sm font-bold text-slate-900 text-center">
                          <span className="font-mono">
                            {formatCurrency(total)}
                          </span>
                        </td>
                      )
                    })}
                    <td className="px-6 py-4 text-center">
                      <Badge variant="outline" className="bg-slate-200 text-slate-800 border-slate-400">
                        Todos
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-slate-900 text-center">
                      <span className="font-mono text-lg">
                        {formatCurrency(displayAnalysis.totalAnual)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Badge variant="outline" className="bg-slate-200 text-slate-800 border-slate-400">
                        {displayData.length} registros
                      </Badge>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Success Message for loaded data */}
        {budgetData.length > 0 && !isLoadingBudgets && (
          <div className="bg-white rounded-xl shadow-sm border border-green-200 p-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-green-900">
                  Datos de Presupuesto {currentYear} Cargados
                </h3>
                <p className="text-green-700">
                  Se han procesado {budgetData.length} departamentos con datos presupuestarios completos para el año {currentYear}.
                  Los datos están listos para su análisis y reportes.
                  {uploadedData.length > 0 && (
                    <span className="block mt-1 text-sm">
                      Última carga: {uploadedData.length} registros desde archivo Excel.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadSuccess}
        isUploading={loading}
        title="Carga Masiva de Presupuesto"
        config={uploadConfig}
        uploadResult={uploadResult}
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span>Confirmar Eliminación</span>
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Esta acción no se puede deshacer. El presupuesto será eliminado permanentemente.
            </DialogDescription>
          </DialogHeader>
          
          {budgetToDelete && (
            <div className="bg-red-50 border border-red-200 rounded p-4 space-y-4">
              <div className="flex items-center space-x-2 mb-3">
                <Trash2 className="h-4 w-4 text-red-600" />
                <span className="font-medium text-red-900">Presupuesto a eliminar:</span>
              </div>
              
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-red-600 uppercase tracking-wide">Departamento</label>
                    <p className="text-sm font-semibold text-red-900">{budgetToDelete.departamento}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-red-600 uppercase tracking-wide">Categoría</label>
                    <p className="text-sm font-semibold text-red-900">{budgetToDelete.categoria}</p>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-red-600 uppercase tracking-wide">Total Anual</label>
                  <p className="text-lg font-bold text-red-900">
                    {budgetToDelete.row && formatCurrency(
                      months.reduce((sum, month) => sum + (budgetToDelete.row[month] || 0), 0)
                    )}
                  </p>
                </div>
                
                {budgetToDelete.row && (
                  <div>
                    <label className="text-xs font-medium text-red-600 uppercase tracking-wide mb-2 block">
                      Desglose Mensual
                    </label>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      {months.map(month => (
                        <div key={month} className="bg-white rounded px-2 py-1 border border-red-200">
                          <span className="font-medium capitalize">{month}:</span>
                          <span className="ml-1 font-mono text-red-800">
                            {formatCurrency(budgetToDelete.row[month] || 0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="space-x-2">
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar Presupuesto
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
} 